import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Edit, Building, MapPin, Mail, Phone, DollarSign, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: '',
    primary_contact: '',
    primary_email: '',
    primary_phone: '',
    sites: [],
    billing_details: {
      billing_name: '',
      billing_address: '',
      billing_email: '',
      billing_phone: '',
      abn: '',
      payment_terms: 'Net 30'
    },
    notes: ''
  });

  const [newSite, setNewSite] = useState({
    site_name: '',
    site_address: '',
    site_contact: '',
    site_phone: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      primary_contact: '',
      primary_email: '',
      primary_phone: '',
      sites: [],
      billing_details: {
        billing_name: '',
        billing_address: '',
        billing_email: '',
        billing_phone: '',
        abn: '',
        payment_terms: 'Net 30'
      },
      notes: ''
    });
    setNewSite({
      site_name: '',
      site_address: '',
      site_contact: '',
      site_phone: ''
    });
    setIsEditMode(false);
    setCurrentClientId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (isEditMode) {
        await axios.put(
          `${API}/clients/${currentClientId}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('Client updated successfully!');
      } else {
        await axios.post(
          `${API}/clients`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('Client created successfully!');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
      console.error('Error saving client:', error);
    }
  };

  const openEditDialog = (client) => {
    setFormData({
      client_name: client.client_name,
      primary_contact: client.primary_contact,
      primary_email: client.primary_email,
      primary_phone: client.primary_phone,
      sites: client.sites || [],
      billing_details: client.billing_details,
      notes: client.notes || ''
    });
    setCurrentClientId(client.id);
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  const deleteClient = async (clientId) => {
    const token = localStorage.getItem('token');
    if (!confirm('Are you sure you want to delete this client?')) return;

    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Client deleted successfully!');
      fetchClients();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete client';
      toast.error(errorMsg);
      console.error('Error deleting client:', error);
    }
  };

  const addSite = () => {
    if (!newSite.site_name || !newSite.site_address) {
      toast.error('Site name and address are required');
      return;
    }

    setFormData({
      ...formData,
      sites: [...formData.sites, newSite]
    });
    setNewSite({
      site_name: '',
      site_address: '',
      site_contact: '',
      site_phone: ''
    });
  };

  const removeSite = (index) => {
    setFormData({
      ...formData,
      sites: formData.sites.filter((_, i) => i !== index)
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="clients-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Client Management</h1>
              <p className="text-gray-400">Manage clients, sites, and billing details</p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-client-btn" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0">
                <Plus className="w-5 h-5 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">
                  {isEditMode ? 'Edit Client' : 'Add New Client'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Client Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-white/10 pb-2">Client Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client_name" className="text-gray-300">Client Name *</Label>
                      <Input
                        id="client_name"
                        data-testid="client-name-input"
                        value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="primary_contact" className="text-gray-300">Primary Contact *</Label>
                      <Input
                        id="primary_contact"
                        data-testid="primary-contact-input"
                        value={formData.primary_contact}
                        onChange={(e) => setFormData({ ...formData, primary_contact: e.target.value })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="primary_email" className="text-gray-300">Primary Email *</Label>
                      <Input
                        id="primary_email"
                        data-testid="primary-email-input"
                        type="email"
                        value={formData.primary_email}
                        onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="primary_phone" className="text-gray-300">Primary Phone *</Label>
                      <Input
                        id="primary_phone"
                        data-testid="primary-phone-input"
                        type="tel"
                        value={formData.primary_phone}
                        onChange={(e) => setFormData({ ...formData, primary_phone: e.target.value })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Sites */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-white/10 pb-2">Sites</h3>
                  
                  {/* Existing Sites */}
                  {formData.sites.length > 0 && (
                    <div className="space-y-2">
                      {formData.sites.map((site, index) => (
                        <div key={index} className="bg-white/5 p-3 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{site.site_name}</p>
                            <p className="text-gray-400 text-sm">{site.site_address}</p>
                            {site.site_contact && <p className="text-gray-400 text-sm">{site.site_contact} - {site.site_phone}</p>}
                          </div>
                          <Button
                            type="button"
                            onClick={() => removeSite(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Site */}
                  <div className="bg-white/5 p-4 rounded-lg space-y-3">
                    <p className="text-sm text-gray-400">Add New Site</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Input
                        placeholder="Site Name"
                        value={newSite.site_name}
                        onChange={(e) => setNewSite({ ...newSite, site_name: e.target.value })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <Input
                        placeholder="Site Address"
                        value={newSite.site_address}
                        onChange={(e) => setNewSite({ ...newSite, site_address: e.target.value })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <Input
                        placeholder="Site Contact (Optional)"
                        value={newSite.site_contact}
                        onChange={(e) => setNewSite({ ...newSite, site_contact: e.target.value })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      <Input
                        placeholder="Site Phone (Optional)"
                        value={newSite.site_phone}
                        onChange={(e) => setNewSite({ ...newSite, site_phone: e.target.value })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={addSite}
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Site
                    </Button>
                  </div>
                </div>

                {/* Billing Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-white/10 pb-2">Billing Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Billing Name *</Label>
                      <Input
                        value={formData.billing_details.billing_name}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          billing_details: { ...formData.billing_details, billing_name: e.target.value }
                        })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Billing Email *</Label>
                      <Input
                        type="email"
                        value={formData.billing_details.billing_email}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          billing_details: { ...formData.billing_details, billing_email: e.target.value }
                        })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label className="text-gray-300">Billing Address *</Label>
                      <Input
                        value={formData.billing_details.billing_address}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          billing_details: { ...formData.billing_details, billing_address: e.target.value }
                        })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Billing Phone *</Label>
                      <Input
                        type="tel"
                        value={formData.billing_details.billing_phone}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          billing_details: { ...formData.billing_details, billing_phone: e.target.value }
                        })}
                        required
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">ABN (Optional)</Label>
                      <Input
                        value={formData.billing_details.abn}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          billing_details: { ...formData.billing_details, abn: e.target.value }
                        })}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="12 345 678 901"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Payment Terms</Label>
                      <Input
                        value={formData.billing_details.payment_terms}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          billing_details: { ...formData.billing_details, payment_terms: e.target.value }
                        })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes" className="text-gray-300">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="bg-white/10 border-white/20 text-white resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    data-testid="submit-client-btn" 
                    className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0"
                  >
                    {isEditMode ? 'Update Client' : 'Create Client'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsDialogOpen(false)} 
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Loading clients...</p>
          </div>
        ) : clients.length === 0 ? (
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardContent className="py-12 text-center">
              <Building className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No clients yet. Add your first client!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {clients.map((client, index) => (
              <Card key={client.id} data-testid={`client-card-${index}`} className="bg-white/5 backdrop-blur-sm border-white/10 hover:border-orange-500/30">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-orange-500/20 p-3 rounded-lg">
                          <Building className="w-6 h-6 text-orange-500" />
                        </div>
                        <div>
                          <CardTitle className="text-xl text-white">{client.client_name}</CardTitle>
                          <p className="text-sm text-gray-400">Added {formatDate(client.created_at)}</p>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        {/* Primary Contact */}
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 uppercase">Primary Contact</p>
                          <div className="space-y-1">
                            <p className="text-white">{client.primary_contact}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Mail className="w-4 h-4" />
                              {client.primary_email}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Phone className="w-4 h-4" />
                              {client.primary_phone}
                            </div>
                          </div>
                        </div>

                        {/* Billing Details */}
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 uppercase">Billing Details</p>
                          <div className="space-y-1">
                            <p className="text-white">{client.billing_details.billing_name}</p>
                            <p className="text-sm text-gray-400">{client.billing_details.billing_address}</p>
                            {client.billing_details.abn && (
                              <p className="text-sm text-gray-400">ABN: {client.billing_details.abn}</p>
                            )}
                            <p className="text-sm text-gray-400">Terms: {client.billing_details.payment_terms}</p>
                          </div>
                        </div>
                      </div>

                      {/* Sites */}
                      {client.sites && client.sites.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-500 uppercase mb-2">Sites ({client.sites.length})</p>
                          <div className="grid md:grid-cols-2 gap-2">
                            {client.sites.map((site, sIndex) => (
                              <div key={sIndex} className="bg-white/5 p-3 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-white text-sm font-medium">{site.site_name}</p>
                                    <p className="text-gray-400 text-xs">{site.site_address}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        data-testid={`edit-client-${index}`}
                        onClick={() => openEditDialog(client)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      
                      <Button
                        data-testid={`delete-client-${index}`}
                        onClick={() => deleteClient(client.id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ClientsPage;
